import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Container, Row, Col, Card, Form, Spinner } from "react-bootstrap";
import {
  ArrowLeft,
  BoxArrowRight,
  Bell,
  PersonCircle,
  Phone,
  PcDisplay,
  Tablet,
  QuestionCircle,
  Globe2,
  CalendarEvent,
  Clock,
  BrowserChrome,
  BrowserSafari,
  BrowserEdge,
  BrowserFirefox,
  Search,
} from "react-bootstrap-icons";
import SettingsGear from "../components/SettingsGear";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import "../App.css";

import LOGO_URL from "../img/tmis_logo_orig.png";
import LoginTimeCharts from "../components/LoginTimeCharts";
import ScrollToTopFab from "../components/ScrollToTopFab";

const API_BASE = process.env.REACT_APP_API || "";

const ENDPOINT_TOTAL_REGISTERED = `${API_BASE}/getTotalRegistered`;
const ENDPOINT_TOTAL_REGISTERED_BY_DATE = `${API_BASE}/getTotalRegisteredByDate`;
const ENDPOINT_TOTAL_UNREGISTERED = `${API_BASE}/getTotalUnregistered`;
const ENDPOINT_USER_COMPLETION = `${API_BASE}/getUserCompletion`;
const ENDPOINT_LATEST_LOGIN = `${API_BASE}/getLatestLogin`;
const ENDPOINT_TG_VALID = `${API_BASE}/getTGValid`;
const ENDPOINT_TG_EXPIRING_SOON = `${API_BASE}/getTGExpiringSoon`;
const ENDPOINT_TG_EXPIRED = `${API_BASE}/getTGExpired`;
const ENDPOINT_COMMON_DEVICE = `${API_BASE}/getCommonDevice`;
const ENDPOINT_COUNTS_BY_STATUS = `${API_BASE}/getCountsByStatus`;

const NAV_BLUE = "#183B72";
const DASH_BG = "#f7f9fc";

function safeParse(json, fallback = {}) {
  try {
    return JSON.parse(json || "") ?? fallback;
  } catch {
    return fallback;
  }
}

function getStoredUser() {
  const user = safeParse(localStorage.getItem("user"), {});
  const displayName =
    localStorage.getItem("display_name") ||
    user.display_name ||
    user.username ||
    localStorage.getItem("username") ||
    "User Acc";

  return { displayName };
}

function formatTimeAgo(dateStr) {
  if (!dateStr) return "";
  const now = new Date();
  const d = new Date(dateStr);
  if (Number.isNaN(+d)) return "";

  const diffMs = now.getTime() - d.getTime();
  const diffMin = Math.floor(diffMs / 60000);
  if (diffMin < 1) return "Just now";
  if (diffMin < 60) return `${diffMin} minute${diffMin > 1 ? "s" : ""} ago`;
  const diffH = Math.floor(diffMin / 60);
  if (diffH < 24) return `${diffH} hour${diffH > 1 ? "s" : ""} ago`;
  const diffD = Math.floor(diffH / 24);
  if (diffD === 1) return "Yesterday";
  return `${diffD} days ago`;
}

function formatDateDisplay(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(+d)) return "";
  const dd = String(d.getDate()).padStart(2, "0");
  const mmm = d.toLocaleString("en", { month: "short" });
  const yyyy = d.getFullYear();
  return `${dd} ${mmm} ${yyyy}`;
}

function formatTimeDisplay(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(+d)) return "";
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

function getWeekNumber(date) {
  const d = new Date(
    Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
  );
  let dayNum = d.getUTCDay();
  if (dayNum === 0) dayNum = 7;

  d.setUTCDate(d.getUTCDate() + 4 - dayNum);

  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return weekNo;
}

function formatYMD(date) {
  const dd = String(date.getDate()).padStart(2, "0");
  const mm = String(date.getMonth() + 1).padStart(2, "0");
  const yyyy = date.getFullYear();
  return `${yyyy}-${mm}-${dd}`;
}

function getMonthStartEnd() {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth(), 1);
  const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);

  return {
    dateFrom: formatYMD(start),
    dateTo: formatYMD(end),
  };
}

function getInitials(name = "") {
  const trimmed = name.trim();
  if (!trimmed) return "?";
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function normalizeLabel(v) {
  if (!v) return "-";
  const s = String(v).trim();
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function getDeviceIcon(device) {
  const d = (device || "").toLowerCase();

  if (d.includes("mobile") || d.includes("phone")) {
    return (
      <span className="device-icon-3d device-icon-3d--phone me-2">
        <Phone size={16} />
      </span>
    );
  }

  if (d.includes("desktop") || d.includes("pc") || d.includes("laptop")) {
    return (
      <span className="device-icon-3d device-icon-3d--desktop me-2">
        <PcDisplay size={16} />
      </span>
    );
  }

  if (d.includes("tablet") || d.includes("ipad")) {
    return (
      <span className="device-icon-3d device-icon-3d--tablet me-2">
        <Tablet size={16} />
      </span>
    );
  }

  return (
    <span className="device-icon-3d device-icon-3d--unknown me-2">
      <QuestionCircle size={16} />
    </span>
  );
}

function getBrowserIcon(browser) {
  const b = (browser || "").toLowerCase();

  if (b.includes("chrome")) {
    return (
      <span className="device-icon-3d device-icon-3d--chrome me-2">
        <BrowserChrome size={16} />
      </span>
    );
  }

  if (b.includes("safari")) {
    return (
      <span className="device-icon-3d device-icon-3d--safari me-2">
        <BrowserSafari size={16} />
      </span>
    );
  }

  if (b.includes("edge")) {
    return (
      <span className="device-icon-3d device-icon-3d--edge me-2">
        <BrowserEdge size={16} />
      </span>
    );
  }

  if (b.includes("firefox")) {
    return (
      <span className="device-icon-3d device-icon-3d--firefox me-2">
        <BrowserFirefox size={16} />
      </span>
    );
  }

  return (
    <span className="device-icon-3d device-icon-3d--unknown me-2">
      <Globe2 size={16} />
    </span>
  );
}

function mapStatusCounts(rows = []) {
  const base = {
    Approved: 0,
    Pending: 0,
    "Re-submit": 0,
    Rejected: 0,
  };

  rows.forEach((row) => {
    const name = String(row.status_name || "")
      .trim()
      .toLowerCase();
    const total = Number(row.totalNo || 0);

    if (name.includes("approved")) base.Approved += total;
    else if (name.includes("pending")) base.Pending += total;
    else if (name.includes("re-submit") || name.includes("resubmit"))
      base["Re-submit"] += total;
    else if (name.includes("rejected")) base.Rejected += total;
  });

  return base;
}

export default function Dashboard() {
  const { displayName } = useMemo(getStoredUser, []);
  const navigate = useNavigate();

  const [period, setPeriod] = useState("today");

  const initialRange = useMemo(getMonthStartEnd, []);

  const [dateRange, setDateRange] = useState({
    dateFrom: initialRange.dateFrom,
    dateTo: initialRange.dateTo,
  });
  const [filterRange, setFilterRange] = useState(dateRange);
  const [summary, setSummary] = useState({
    totalRegistered: 0,
    newRegistration: 0,
    pendingRegistration: 0,
    guideWithValidLicences: 0,
    guideWithValidLicences2: 0,
    soonExpired: { gt90: 0, d30: 0, d60: 0, d90: 0 },
  });

  const [recent, setRecent] = useState([]);
  const [completion, setCompletion] = useState({
    verified: 0,
    unverified: 0,
  });

  const [deviceStats, setDeviceStats] = useState({
    devices: [],
    browsers: [],
    os: [],
  });

  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [minutesPassed, setMinutesPassed] = useState(0);

  const [activityStatus, setActivityStatus] = useState({
    learn: {
      Approved: 0,
      Pending: 0,
      "Re-submit": 0,
      Rejected: 0,
    },
    do: {
      Approved: 0,
      Pending: 0,
      "Re-submit": 0,
      Rejected: 0,
    },
  });

  const handleLogout = () => {
    localStorage.clear();
    sessionStorage.clear();
    navigate("/", { replace: true });
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate("/", { replace: true });
  };

  const loadDashboard = useCallback(async () => {
    if (!API_BASE) return;
    setLoading(true);
    setErr("");

    try {
      const token =
        localStorage.getItem("token") ||
        localStorage.getItem("authToken") ||
        localStorage.getItem("auth_token") ||
        "";

      const headers = {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      };

      const now = new Date();
      const year = now.getFullYear();

      let periodType = "daily";
      if (period === "thisWeek") periodType = "weekly";
      else if (period === "thisMonth") periodType = "monthly";

      const paramsNew = {
        type: periodType,
        year,
      };

      if (periodType === "daily") {
        paramsNew.date = formatYMD(now);
      } else if (periodType === "weekly") {
        paramsNew.week = getWeekNumber(now);
      } else if (periodType === "monthly") {
        paramsNew.month = now.getMonth() + 1;
      }

      const [
        resTotalRegistered,
        resTotalByDate,
        resTotalUnregistered,
        resCompletion,
        resLatestLogin,
        resTGValid,
        resTGSoon,
        resTGExpired,
        resCommonDevice,
        resStatusCounts,
      ] = await Promise.all([
        axios.get(ENDPOINT_TOTAL_REGISTERED, { headers }),
        axios.get(ENDPOINT_TOTAL_REGISTERED_BY_DATE, {
          headers,
          params: paramsNew,
        }),
        axios.get(ENDPOINT_TOTAL_UNREGISTERED, { headers }),
        axios.get(ENDPOINT_USER_COMPLETION, { headers }),
        axios.get(ENDPOINT_LATEST_LOGIN, { headers }),
        axios.get(ENDPOINT_TG_VALID, { headers }),
        axios.get(ENDPOINT_TG_EXPIRING_SOON, { headers }),
        axios.get(ENDPOINT_TG_EXPIRED, { headers }),
        axios.get(ENDPOINT_COMMON_DEVICE, { headers }),
        axios.get(ENDPOINT_COUNTS_BY_STATUS, {
          headers,
          params: {
            dateFrom: filterRange.dateFrom,
            dateTo: filterRange.dateTo,
          },
        }),
      ]);

      const totalRegistered =
        resTotalRegistered.data?.data?.[0]?.totalRegistered ?? 0;

      const newRegistration = resTotalByDate.data?.data?.ctr ?? 0;

      const pendingRegistration = resTotalUnregistered.data?.data?.ctr ?? 0;

      const verified = resCompletion.data?.data?.ctrVerified ?? 0;
      const unverified = resCompletion.data?.data?.ctrUnverified ?? 0;

      const listLatest = resLatestLogin.data?.data ?? [];

      const guideValid = (resTGValid.data?.data ?? []).length;
      const guideExpired = (resTGExpired.data?.data ?? []).length;

      const soonData = resTGSoon.data?.data || {};
      const soon30 = (soonData.data30 ?? []).length;
      const soon60 = (soonData.data60 ?? []).length;
      const soon90 = (soonData.data90 ?? []).length;
      const more90 = (soonData.dataMore ?? []).length;

      const deviceCtr = resCommonDevice.data?.data?.deviceCtr ?? [];
      const browserCtr = resCommonDevice.data?.data?.browserCtr ?? [];
      const osCtr = resCommonDevice.data?.data?.osCtr ?? [];

      setSummary({
        totalRegistered,
        newRegistration,
        pendingRegistration,
        guideWithValidLicences: guideValid,
        guideWithValidLicences2: guideExpired,
        soonExpired: {
          gt90: more90,
          d30: soon30,
          d60: soon60,
          d90: soon90,
        },
      });

      setCompletion({ verified, unverified });

      setDeviceStats({
        devices: deviceCtr,
        browsers: browserCtr,
        os: osCtr,
      });

      setActivityStatus({
        learn: mapStatusCounts(
          resStatusCounts.data?.data?.countStatusLearn || [],
        ),
        do: mapStatusCounts(resStatusCounts.data?.data?.countStatusDo || []),
      });

      const mapped = listLatest.map((row, idx) => {
        const firstName = row.first_name || "";
        const lastName = row.last_name || "";
        const nameCombined = `${firstName} ${lastName}`.trim();
        const displayName = nameCombined || row.username || "-";

        const email = row.username || "";
        const phone = row.phone_number || "";
        const created = row.activity_date;

        return {
          id: row.user_id ?? idx + 1,
          name: displayName,
          initials: getInitials(displayName),
          avatarKey: displayName || String(row.user_id ?? idx + 1),
          badge_no: row.badge_no ?? "-",
          email,
          phone,
          timeAgo: formatTimeAgo(created),
          expiry_date: row.tg_expiry_date,
          created_date: created,
        };
      });

      setRecent(mapped);
      setActiveIndex(0);

      const stamp = new Date().toISOString();
      setLastUpdated(stamp);
      setMinutesPassed(0);
    } catch (e) {
      console.error(e);
      setErr("Server error while loading dashboard.");
    } finally {
      setLoading(false);
    }
  }, [period, filterRange.dateFrom, filterRange.dateTo]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  useEffect(() => {
    const intervalId = setInterval(
      () => {
        loadDashboard();
      },
      5 * 60 * 1000,
    );
    return () => clearInterval(intervalId);
  }, [loadDashboard]);

  useEffect(() => {
    if (!lastUpdated) return;

    setMinutesPassed(0);

    const timer = setInterval(() => {
      setMinutesPassed((prev) => {
        if (prev >= 5) return 5;
        return prev + 1;
      });
    }, 60 * 1000);

    return () => clearInterval(timer);
  }, [lastUpdated]);

  function renderMinuteProgress(count) {
    const max = 5;
    const circles = [];

    for (let i = 0; i < max; i++) {
      circles.push(
        <span
          key={i}
          style={{
            display: "inline-block",
            width: 8,
            height: 8,
            borderRadius: "50%",
            marginLeft: 2,
            background: i < count ? "#3d74f5" : "#d0d6e2",
            transition: "0.3s",
          }}
        ></span>,
      );
    }

    return circles;
  }

  const circleBadgeBase = {
    width: 72,
    height: 72,
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: 24,
    fontWeight: 700,
  };

  const mainRecord = recent[activeIndex] || null;
  const remaining = Math.max(0, recent.length - (activeIndex + 1));
  const canPrev = activeIndex > 0;
  const canNext = activeIndex < recent.length - 1;

  const goPrev = () => {
    if (canPrev) setActiveIndex((idx) => idx - 1);
  };

  const goNext = () => {
    if (canNext) setActiveIndex((idx) => idx + 1);
  };

  const totalCompletion = completion.verified + completion.unverified;
  const verifiedPct =
    totalCompletion > 0
      ? Math.round((completion.verified / totalCompletion) * 100)
      : 0;
  const unverifiedPct =
    totalCompletion > 0
      ? Math.round((completion.unverified / totalCompletion) * 100)
      : 0;

  const deviceList = useMemo(() => {
    const list = deviceStats.devices || [];
    return [...list].sort(
      (a, b) => Number(b.ctrDevice || 0) - Number(a.ctrDevice || 0),
    );
  }, [deviceStats]);

  const osList = useMemo(() => {
    const list = deviceStats.os || [];
    return [...list].sort(
      (a, b) => Number(b.ctrOs || 0) - Number(a.ctrOs || 0),
    );
  }, [deviceStats]);

  const browserList = useMemo(() => {
    const list = deviceStats.browsers || [];
    return [...list].sort(
      (a, b) => Number(b.ctrBrowser || 0) - Number(a.ctrBrowser || 0),
    );
  }, [deviceStats]);

  const totalDeviceSessions = deviceList.reduce(
    (sum, row) => sum + Number(row.ctrDevice || 0),
    0,
  );
  const totalOsSessions = osList.reduce(
    (sum, row) => sum + Number(row.ctrOs || 0),
    0,
  );
  const totalBrowserSessions = browserList.reduce(
    (sum, row) => sum + Number(row.ctrBrowser || 0),
    0,
  );

  return (
    <div
      className="custom-font"
      style={{ minHeight: "100vh", background: DASH_BG }}
    >
      <Container>
        <div
          className="d-flex align-items-center justify-content-between py-2"
          style={{ gap: 16 }}
        >
          <img src={LOGO_URL} alt="TMIS" style={{ height: 50 }} />
          <div className="d-flex align-items-center" style={{ gap: 25 }}>
            <Bell size={20} color="#1A3584" />
            <SettingsGear />
            <BoxArrowRight
              size={20}
              color="#1A3584"
              style={{ cursor: "pointer" }}
              onClick={handleLogout}
              role="button"
              aria-label="Logout"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleLogout();
              }}
            />
            <span>{displayName}</span>
            <PersonCircle size={23} color="#1A3584" />
          </div>
        </div>
      </Container>

      <div
        style={{
          height: 64,
          background: NAV_BLUE,
          color: "white",
          display: "flex",
          alignItems: "center",
          marginTop: 8,
        }}
      >
        <Container>
          <div className="d-flex align-items-center" style={{ gap: 12 }}>
            <span
              role="button"
              aria-label="Go back"
              tabIndex={0}
              onClick={handleBack}
              onKeyDown={(e) =>
                e.key === "Enter" || e.key === " " ? handleBack() : null
              }
              style={{
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                width: 36,
                height: 36,
                background: "transparent",
                cursor: "pointer",
              }}
            >
              <ArrowLeft size={18} color="#ffffff" />
            </span>
            <h4 className="m-0 fw-bold">DASHBOARD</h4>
          </div>
        </Container>
      </div>

      <Container style={{ paddingTop: 22, paddingBottom: 40 }}>
        <Card className="extra-white-card mb-4">
          <Card.Body className="extra-white-body">
            <Row className="g-3 align-items-end">
              <Col md={4}>
                <Form.Label className="fw-semibold small">From Date</Form.Label>
                <Form.Control
                  type="date"
                  value={dateRange.dateFrom}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      dateFrom: e.target.value,
                    }))
                  }
                  style={{
                    backgroundColor: "#ffffff",
                    color: "#1a1a1a",
                    fontWeight: "500",
                  }}
                />
              </Col>

              <Col md={4}>
                <Form.Label className="fw-semibold small">To Date</Form.Label>
                <Form.Control
                  type="date"
                  value={dateRange.dateTo}
                  onChange={(e) =>
                    setDateRange((prev) => ({
                      ...prev,
                      dateTo: e.target.value,
                    }))
                  }
                  style={{
                    backgroundColor: "#ffffff",
                    color: "#1a1a1a",
                    fontWeight: "500",
                  }}
                />
              </Col>

              <Col md={2}>
                <button
                  className="btn btn-primary w-100"
                  style={{
                    height: "38px",
                    fontWeight: "600",
                    background: "#183B72",
                    border: "none",
                  }}
                  onClick={() => {
                    setFilterRange(dateRange); // 🔥 trigger API
                  }}
                >
                  {loading ? (
                    <Spinner size="sm" />
                  ) : (
                    <span className="d-flex align-items-center justify-content-center gap-2">
                      <Search size={16} />
                      Search
                    </span>
                  )}
                </button>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        <Row className="g-4 mb-4">
          {[
            {
              title: "LEARN",
              data: activityStatus.learn,
              color: "#183B72",
            },
            {
              title: "DO",
              data: activityStatus.do,
              color: "#15803d",
            },
          ].map((card) => (
            <Col md={6} key={card.title}>
              <Card className="extra-white-card h-100">
                <Card.Body className="extra-white-body">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <h6 className="mb-0 fw-bold">{card.title} Overview</h6>
                    {loading && <Spinner animation="border" size="sm" />}
                  </div>

                  <Row className="g-3 text-center">
                    <Col xs={6} lg={3}>
                      <div className="status-mini-card status-approved">
                        <div className="status-count">{card.data.Approved}</div>
                        <div className="status-label">Approved</div>
                      </div>
                    </Col>

                    <Col xs={6} lg={3}>
                      <div className="status-mini-card status-pending">
                        <div className="status-count">{card.data.Pending}</div>
                        <div className="status-label">Pending</div>
                      </div>
                    </Col>

                    <Col xs={6} lg={3}>
                      <div className="status-mini-card status-resubmit">
                        <div className="status-count">
                          {card.data["Re-submit"]}
                        </div>
                        <div className="status-label">Resubmit</div>
                      </div>
                    </Col>

                    <Col xs={6} lg={3}>
                      <div className="status-mini-card status-rejected">
                        <div className="status-count">{card.data.Rejected}</div>
                        <div className="status-label">Rejected</div>
                      </div>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
        <h6 className="mb-1 fw-bold">Registration &amp; Onboarding Metrics</h6>

        <div className="mb-3">
          <span
            style={{
              fontSize: 11,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            Refresh at{" "}
            {lastUpdated
              ? `${formatDateDisplay(lastUpdated)} • ${formatTimeDisplay(
                  lastUpdated,
                )}`
              : "—"}
            <span style={{ display: "flex", alignItems: "center" }}>
              {renderMinuteProgress(minutesPassed)}
            </span>
          </span>
        </div>

        <Row className="g-4 mb-4 align-items-stretch">
          <Col md={4} lg={3}>
            <div className="d-flex flex-column gap-3 h-100">
              <Card className="metric-card metric-card--teal">
                <Card.Body className="metric-card-body">
                  <div className="metric-label">Total Registered TGs</div>
                  <div className="metric-value">{summary.totalRegistered}</div>
                </Card.Body>
              </Card>

              <Card className="metric-card metric-card--blue">
                <Card.Body className="metric-card-body">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="metric-label">New Registration</div>
                      <div className="metric-value">
                        {summary.newRegistration}
                      </div>
                    </div>
                    <Form.Select
                      size="sm"
                      value={period}
                      onChange={(e) => setPeriod(e.target.value)}
                      className="metric-period-select"
                    >
                      <option value="today">Today</option>
                      <option value="thisWeek">This Week</option>
                      <option value="thisMonth">This Month</option>
                    </Form.Select>
                  </div>
                </Card.Body>
              </Card>

              <Card className="metric-card metric-card--orange">
                <Card.Body className="metric-card-body">
                  <div className="metric-label">Pending Registration</div>
                  <div className="metric-value">
                    {summary.pendingRegistration}
                  </div>
                </Card.Body>
              </Card>
            </div>
          </Col>

          <Col md={8} lg={9}>
            <Row className="g-4 h-100 align-items-stretch">
              <Col md={6}>
                <Card className="last-records-card h-100">
                  <Card.Body className="last-records-body">
                    <div className="d-flex justify-content-between align-items-center mb-2">
                      <h6 className="mb-0 fw-semibold">
                        Latest Login Activities
                      </h6>
                      {loading && (
                        <Spinner animation="border" role="status" size="sm" />
                      )}
                    </div>

                    {err ? (
                      <div className="text-danger small mt-3">{err}</div>
                    ) : !recent.length && !loading ? (
                      <div className="text-muted small mt-3">
                        No recent records.
                      </div>
                    ) : mainRecord ? (
                      <div className="last-record-wrapper">
                        <div className="last-record-inner">
                          <div className="last-record-extra-badge">
                            +{remaining}
                          </div>

                          <div className="last-record-header-row">
                            <span className="last-record-badge-label">
                              Badge No
                            </span>
                            <span className="last-record-badge-value">
                              {mainRecord.badge_no}
                            </span>
                          </div>

                          <div className="last-record-datetime-pill">
                            <div className="last-record-datetime-item">
                              <CalendarEvent size={14} className="me-1" />
                              <span>
                                {formatDateDisplay(mainRecord.created_date)}
                              </span>
                            </div>
                            <div className="last-record-datetime-item">
                              <Clock size={14} className="me-1" />
                              <span>
                                {formatTimeDisplay(mainRecord.created_date)}
                              </span>
                            </div>
                          </div>

                          <div className="last-record-name">
                            {mainRecord.name}
                          </div>
                          <div className="last-record-email">
                            {mainRecord.email}
                          </div>
                          <div className="last-record-phone">
                            {mainRecord.phone}
                          </div>
                          <div className="last-record-time">
                            {mainRecord.timeAgo}
                          </div>

                          <div className="last-record-nav mt-3">
                            <button
                              type="button"
                              className="last-record-nav-btn"
                              disabled={!canPrev}
                              onClick={goPrev}
                            >
                              ◀ Previous
                            </button>
                            <span className="last-record-counter">
                              {activeIndex + 1} / {recent.length}
                            </span>
                            <button
                              type="button"
                              className="last-record-nav-btn"
                              disabled={!canNext}
                              onClick={goNext}
                            >
                              Next ▶
                            </button>
                          </div>
                        </div>
                      </div>
                    ) : null}
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="extra-white-card h-100">
                  <Card.Body className="extra-white-body">
                    <h6 className="mb-3 fw-semibold">
                      Devices, OS &amp; Browsers
                    </h6>
                    <div className="text-muted small mb-3">
                      Most common devices, operating systems, and browsers used
                      to access LEARN + DO.
                    </div>

                    {loading &&
                    !deviceList.length &&
                    !osList.length &&
                    !browserList.length ? (
                      <div className="text-center py-3">
                        <Spinner animation="border" role="status" size="sm" />
                      </div>
                    ) : (
                      <div className="device-scroll-area">
                        <Row className="g-3">
                          <Col xs={12} md={6}>
                            {/* Devices */}
                            <div className="small fw-semibold mb-1">
                              Devices
                            </div>
                            {deviceList.length ? (
                              <table className="table table-sm mb-0 align-middle small">
                                <tbody>
                                  {deviceList.map((row, idx) => {
                                    const label = normalizeLabel(row.device);
                                    const count = Number(row.ctrDevice || 0);
                                    const pct =
                                      totalDeviceSessions > 0
                                        ? Math.round(
                                            (count / totalDeviceSessions) * 100,
                                          )
                                        : 0;
                                    return (
                                      <tr key={`${label}-${idx}`}>
                                        <td>
                                          {getDeviceIcon(label)}
                                          {label}
                                        </td>
                                        <td className="text-end">
                                          {count}
                                          {totalDeviceSessions > 0 && (
                                            <span className="text-muted small ms-1">
                                              ({pct}%)
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            ) : (
                              <div className="text-muted small">
                                No device data.
                              </div>
                            )}

                            <div className="small fw-semibold mb-1 mt-2">
                              Browsers
                            </div>
                            {browserList.length ? (
                              <table className="table table-sm mb-0 align-middle small">
                                <tbody>
                                  {browserList.map((row, idx) => {
                                    const label = normalizeLabel(row.browser);
                                    const count = Number(row.ctrBrowser || 0);
                                    const pct =
                                      totalBrowserSessions > 0
                                        ? Math.round(
                                            (count / totalBrowserSessions) *
                                              100,
                                          )
                                        : 0;
                                    return (
                                      <tr key={`${label}-${idx}`}>
                                        <td>
                                          {getBrowserIcon(label)}
                                          {label}
                                        </td>
                                        <td className="text-end">
                                          {count}
                                          {totalBrowserSessions > 0 && (
                                            <span className="text-muted small ms-1">
                                              ({pct}%)
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            ) : (
                              <div className="text-muted small">
                                No browser data.
                              </div>
                            )}
                          </Col>

                          {/* Browsers */}
                          <Col xs={12} md={6}>
                            {/* OS */}
                            <div className="small fw-semibold mb-1">
                              Operating Systems
                            </div>
                            {osList.length ? (
                              <table className="table table-sm mb-0 align-middle small">
                                <tbody>
                                  {osList.map((row, idx) => {
                                    const label = normalizeLabel(row.os);
                                    const count = Number(row.ctrOs || 0);
                                    const pct =
                                      totalOsSessions > 0
                                        ? Math.round(
                                            (count / totalOsSessions) * 100,
                                          )
                                        : 0;
                                    return (
                                      <tr key={`${label}-${idx}`}>
                                        <td>{label}</td>
                                        <td className="text-end">
                                          {count}
                                          {totalOsSessions > 0 && (
                                            <span className="text-muted small ms-1">
                                              ({pct}%)
                                            </span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            ) : (
                              <div className="text-muted small">
                                No OS data.
                              </div>
                            )}
                          </Col>
                        </Row>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>

        <h6 className="mb-3 mt-2 fw-bold">Compliance &amp; Validity Metrics</h6>

        <Row className="g-4 align-items-stretch">
          <Col md={4} lg={3}>
            <div className="d-flex flex-column gap-3 h-100">
              <Card className="metric-card metric-card--purple">
                <Card.Body className="metric-card-body">
                  <div className="metric-label">TGs with Valid Licences</div>
                  <div className="metric-value">
                    {summary.guideWithValidLicences}
                  </div>
                </Card.Body>
              </Card>

              <Card className="metric-card metric-card--red">
                <Card.Body className="metric-card-body">
                  <div className="metric-label">TGs with Expired Licenses</div>
                  <div className="metric-value">
                    {summary.guideWithValidLicences2}
                  </div>
                </Card.Body>
              </Card>
            </div>
          </Col>

          <Col md={8} lg={9}>
            <Row className="g-4 h-100 align-items-stretch">
              <Col md={6}>
                <Card className="soon-expired-card h-100">
                  <Card.Body className="soon-expired-body">
                    <h6 className="mb-3 fw-semibold">
                      TG Licence Expiry Overview
                    </h6>
                    <Row className="text-center h-100 align-items-center">
                      <Col md={3} className="mb-3 mb-md-0">
                        <div className="d-flex flex-column align-items-center gap-2">
                          <div
                            style={{
                              ...circleBadgeBase,
                              background: "#ea580c",
                              color: "white",
                            }}
                          >
                            {summary.soonExpired.d30}
                          </div>
                          <div style={{ fontSize: 13, fontWeight: "bold" }}>
                            {"\u2264"} 30 days <br />
                            remaining
                          </div>
                        </div>
                      </Col>
                      <Col md={3} className="mb-3 mb-md-0">
                        <div className="d-flex flex-column align-items-center gap-2">
                          <div
                            style={{
                              ...circleBadgeBase,
                              background: "#d97706",
                              color: "white",
                            }}
                          >
                            {summary.soonExpired.d60}
                          </div>
                          <div style={{ fontSize: 13, fontWeight: "bold" }}>
                            31-60 days <br />
                            remaining
                          </div>
                        </div>
                      </Col>
                      <Col md={3}>
                        <div className="d-flex flex-column align-items-center gap-2">
                          <div
                            style={{
                              ...circleBadgeBase,
                              background: "#3d74f5",
                              color: "white",
                            }}
                          >
                            {summary.soonExpired.d90}
                          </div>
                          <div style={{ fontSize: 13, fontWeight: "bold" }}>
                            61-90 days <br />
                            remaining
                          </div>
                        </div>
                      </Col>
                      <Col md={3} className="mb-3 mb-md-0">
                        <div className="d-flex flex-column align-items-center gap-2">
                          <div
                            style={{
                              ...circleBadgeBase,
                              background: "#16a34a",
                              color: "white",
                            }}
                          >
                            {summary.soonExpired.gt90}
                          </div>
                          <div style={{ fontSize: 13, fontWeight: "bold" }}>
                            &gt; 90 Days <br />
                            remaining
                          </div>
                        </div>
                      </Col>
                    </Row>
                  </Card.Body>
                </Card>
              </Col>

              <Col md={6}>
                <Card className="extra-white-card h-100">
                  <Card.Body className="extra-white-body">
                    <h6 className="mb-3 fw-semibold">Completion Status</h6>
                    <div className="text-muted small mb-3">
                      Verified vs Non-verified TG accounts.
                    </div>

                    <div className="d-flex flex-column gap-2">
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="fw-semibold">Verified</span>
                        <span className="text-muted small">
                          {completion.verified} TGs ({verifiedPct}%)
                        </span>
                      </div>
                      <div className="progress" style={{ height: 8 }}>
                        <div
                          className="progress-bar"
                          role="progressbar"
                          style={{ width: `${verifiedPct}%` }}
                          aria-valuenow={verifiedPct}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        />
                      </div>

                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <span className="fw-semibold">Not Verified</span>
                        <span className="text-muted small">
                          {completion.unverified} TGs ({unverifiedPct}%)
                        </span>
                      </div>
                      <div className="progress" style={{ height: 8 }}>
                        <div
                          className="progress-bar bg-secondary"
                          role="progressbar"
                          style={{ width: `${unverifiedPct}%` }}
                          aria-valuenow={unverifiedPct}
                          aria-valuemin={0}
                          aria-valuemax={100}
                        />
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </Col>
        </Row>

        <Row className="g-4 mb-4 mt-2">
          <Col md={12}>
            <Card className="extra-white-card">
              <Card.Body className="extra-white-body">
                <h6 className="mb-1 fw-bold">
                  Most Active Login Time (Daily / Weekly / Monthly)
                </h6>
                <LoginTimeCharts />
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </Container>
      <ScrollToTopFab />
    </div>
  );
}
